// umlaut-check: skip — "AenderungsZeitleiste"/"aenderungen" sind
// Komponenten- bzw. Prop-Namen, "bestaetigt" ist ein Status-Enum-Wert aus
// cosmos_lieferpositionen; alles technische ASCII-Bezeichner.

import React, {useCallback, useEffect, useState} from "react";
import {Link} from "@inertiajs/react";
import {OverlayTrigger, Spinner, Tooltip} from "react-bootstrap";
import axios from "axios";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import AmpelSelect, {AmpelPunkt, AMPEL_LABEL} from "./AmpelSelect.jsx";
import AenderungsZeitleiste from "./AenderungsZeitleiste.jsx";
import {formatCosmosWoche, formatKw, formatMenge, lieferpositionStatusLabel, markeLabel} from "./format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

function PositionZeile({position, canEdit, onGespeichert}) {
    const {addToast} = useExternalControls();
    const [mengeZusage, setMengeZusage] = useState(String(position.menge_zusage ?? 0));
    const [ampel, setAmpel] = useState(position.ampel_gespeichert || "");
    const [speichertLaeuft, setSpeichertLaeuft] = useState(false);

    const speichern = () => {
        setSpeichertLaeuft(true);
        axios
            .post("/cosmos/daten/lieferplan/zelle", {
                position_id: position.id,
                menge_zusage: mengeZusage === "" ? null : Number(mengeZusage),
                ampel: ampel === "" ? null : ampel,
            })
            .then(({data}) => {
                addToast(<FlexToast variant="success" title="Lieferplan" message="Position gespeichert." />);
                onGespeichert(data);
            })
            .catch((e) => {
                addToast(<FlexToast variant="danger" title="Lieferplan" autohide={false} message={e.response?.data?.message || "Speichern fehlgeschlagen."} />);
            })
            .finally(() => setSpeichertLaeuft(false));
    };

    return (
        <div className="border rounded-1 p-2 mb-2">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                    <div className="fw-semibold small">
                        {position.lieferung ? `${markeLabel(position.lieferung.marke)} · ${position.lieferung.zielort || "–"}` : "Ohne Lieferung"}
                    </div>
                    <div className="small text-muted">Status {lieferpositionStatusLabel(position.status)}</div>
                </div>
                <div className="small text-end">
                    <div>Wunsch {formatMenge(position.menge_wunsch)}</div>
                    <div>Ist {formatMenge(position.menge_ist)}</div>
                    {position.menge_vorschlag_neu ? <div>Neuer Vorschlag {formatMenge(position.menge_vorschlag_neu)}</div> : null}
                </div>
            </div>

            {!canEdit && (
                <div className="d-flex align-items-center gap-2 mt-2 small">
                    <span>Zusage {formatMenge(position.menge_zusage)}</span>
                    <span className="d-inline-flex align-items-center gap-1">
                        <AmpelPunkt farbe={position.ampel} /> {AMPEL_LABEL[position.ampel] || "ohne Farbe"}
                        {position.ampel_errechnet && <span className="text-muted">(errechnet)</span>}
                    </span>
                </div>
            )}

            {canEdit && (
                <div className="d-flex align-items-end flex-wrap gap-2 mt-2">
                    <div>
                        <HeaderWithHelp help="Von Cosmos zugesagte Menge für diese Position - überschreibt den bisherigen Wert nach dem Speichern.">
                            <label htmlFor={`zusage-${position.id}`} className="small text-muted d-block mb-1">
                                Zusage
                            </label>
                        </HeaderWithHelp>
                        <input
                            id={`zusage-${position.id}`}
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                            style={{width: 110}}
                            value={mengeZusage}
                            onChange={(e) => setMengeZusage(e.target.value)}
                        />
                    </div>
                    <div>
                        <HeaderWithHelp help="Ampel von Hand setzen - leer lässt die Tabelle wieder eine Ampel errechnen.">
                            <span className="small text-muted d-block mb-1">Ampel</span>
                        </HeaderWithHelp>
                        <AmpelSelect value={ampel} onChange={(v) => setAmpel(v || "")} ariaLabel="Ampel" />
                    </div>
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-zelle-speichern-${position.id}`}>Speichert Zusage und Ampel dieser Position.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-primary btn-sm" disabled={speichertLaeuft} onClick={speichern}>
                                {speichertLaeuft ? "Speichert …" : "Speichern"}
                            </button>
                        </span>
                    </OverlayTrigger>
                </div>
            )}
        </div>
    );
}

/**
 * Inhalt der Zellen-Detailansicht (AP7 B, PLAN.md Abschnitt 5 "Lieferplan"):
 * Positionen mit allen Mengen, Änderungshistorie, Verhandlung aus Blatt
 * "MOLOTOW (2)" und die Cosmos-Verfügbarkeit des Artikels. Editieren
 * (Zusage/Ampel je Position) nur mit canEdit.
 */
export default function LieferplanZelleDetail({artikelNummer, kw, canEdit, canArtikel, canLieferungen, onZelleAktualisiert}) {
    const [daten, setDaten] = useState(null);
    const [ladefehler, setLadefehler] = useState(null);

    const laden = useCallback(() => {
        if (!artikelNummer || !kw) {
            return undefined;
        }
        const controller = new AbortController();
        setDaten(null);
        setLadefehler(null);
        axios
            .get("/cosmos/daten/lieferplan/zelle", {params: {artikel: artikelNummer, kw}, signal: controller.signal})
            .then(({data}) => setDaten(data))
            .catch((e) => {
                if (!axios.isCancel(e)) {
                    setLadefehler(e.response?.data?.message || "Die Zelle konnte nicht geladen werden.");
                }
            });
        return () => controller.abort();
    }, [artikelNummer, kw]);

    useEffect(() => laden(), [laden]);

    if (ladefehler) {
        return <p className="text-danger">{ladefehler}</p>;
    }

    if (!daten) {
        return (
            <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">Wird geladen…</span>
                </Spinner>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <div className="fw-semibold">{artikelNummer}</div>
                    <div className="small text-muted">{daten.tbd ? "Woche offen (TBD)" : formatKw(daten.kw)}</div>
                </div>
                {canArtikel && (
                    <Link href={`/cosmos/artikel/${artikelNummer}`} className="small">
                        Zum Artikel-Blatt
                    </Link>
                )}
            </div>

            {daten.positionen.length === 0 && <p className="text-muted small">Keine Position für diese Zelle.</p>}

            {daten.positionen.map((p) => (
                <div key={p.id}>
                    <PositionZeile
                        position={p}
                        canEdit={canEdit}
                        onGespeichert={(antwort) => {
                            laden();
                            onZelleAktualisiert?.(antwort);
                        }}
                    />
                    {canLieferungen && p.lieferung && (
                        <Link href={`/cosmos/lieferungen/${p.lieferung.id}`} className="small d-block mb-2">
                            Zur Lieferung {p.lieferung.lkw_bezeichnung || `#${p.lieferung.id}`}
                        </Link>
                    )}
                </div>
            ))}

            {daten.verhandlung.length > 0 && (
                <div className="mt-3">
                    <div className="fw-semibold small mb-1">Verhandlung (Blatt „MOLOTOW (2)“)</div>
                    <ul className="list-unstyled small mb-0">
                        {daten.verhandlung.map((v, i) => (
                            <li key={i} className="border-bottom py-1">
                                {v.zwischenstand ? (
                                    <>
                                        Zwischenstand: {v.original ?? "–"} → {v.aktualisiert ?? "–"}
                                    </>
                                ) : (
                                    <>
                                        Original {v.original ?? "–"} → Final {v.final ?? "–"}
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {daten.verfuegbarkeit.length > 0 && (
                <div className="mt-3">
                    <div className="fw-semibold small mb-1">Cosmos-Verfügbarkeit</div>
                    <table className="table table-sm mb-0">
                        <thead>
                            <tr>
                                <th>
                                    <HeaderWithHelp help="Cosmos-eigene Wochenbezeichnung aus der Ampel-Datei.">Woche</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Verfügbare Menge in Cosmos' Lager für diese Woche.">Menge</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Artikelfamilie laut Ampel-Datei.">Familie</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help="Stand der Ampel-Datei, aus der diese Zeile stammt.">Stand</HeaderWithHelp>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {daten.verfuegbarkeit.map((v, i) => (
                                <tr key={i}>
                                    <td>{formatCosmosWoche(v.kw_bezeichnung)}</td>
                                    <td>{formatMenge(v.menge)}</td>
                                    <td>{v.familie || "–"}</td>
                                    <td>{v.stand_am ? v.stand_am.split("-").reverse().join(".") : "–"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-3">
                <div className="fw-semibold small mb-1">Änderungshistorie</div>
                <AenderungsZeitleiste aenderungen={daten.aenderungen} />
            </div>
        </div>
    );
}
